import React from 'react';
import { Box, Typography, Card, CardContent, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const formatTimestamp = (seconds) => {
  const total = Math.max(0, Math.floor(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const ResultsDisplay = ({ results }) => {
  if (!results || results.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }} className="fade-in">
      <Typography variant="h5" gutterBottom className="gradient-text" sx={{ fontWeight: 600, mb: 3 }}>
        📋 Resultados de Transcripción
      </Typography>
      {results.map((result, index) => (
        <Card key={index} sx={{ mb: 3 }} className="results-card">
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6">{result.original_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Duración: {(result.duration_seconds || 0).toFixed(2)}s | Tamaño: {(result.size_mb || 0).toFixed(2)}MB | Dispositivo: {result.transcription?.device || "unknown"}
            </Typography>
            {result.transcription?.speakers?.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {result.transcription.speakers.map((speaker) => (
                  <Chip
                    key={speaker.id}
                    size="small"
                    label={`${speaker.name} (${Math.round(speaker.duration_seconds || 0)}s)`}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>📝 Transcripción Completa</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {result.transcription?.text || "No hay transcripción disponible"}
                </Typography>
              </AccordionDetails>
            </Accordion>

            {result.transcription?.segments && result.transcription.segments.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>⏱️ Transcripción con Marcas de Tiempo</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {result.transcription.segments.map((segment, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          primary={`[${formatTimestamp(segment.start)} - ${formatTimestamp(segment.end)}] ${segment.speaker ? `${segment.speaker}: ` : ''}${segment.text || ""}`}
                          secondary={`Segmento ${segment.id || i}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>📋 Resumen Inteligente</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{
                  '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1, fontWeight: 600 },
                  '& ul, & ol': { pl: 3 },
                  '& li': { mb: 0.5 },
                  '& p': { mb: 1 },
                  '& strong': { fontWeight: 600 },
                  '& hr': { my: 2, border: 'none', borderTop: '1px solid rgba(0,0,0,0.1)' },
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                    marginBottom: 2,
                    border: '1px solid rgba(0,0,0,0.1)'
                  },
                  '& th, & td': {
                    border: '1px solid rgba(0,0,0,0.1)',
                    padding: '8px 12px',
                    textAlign: 'left'
                  },
                  '& th': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    fontWeight: 600
                  },
                  '& tr:nth-of-type(even)': {
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }
                }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.summary || "No hay resumen disponible"}</ReactMarkdown>
                </Box>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default ResultsDisplay;
